import {
    ConfirmationModel,
    CONNECTION_ERROR_CODES,
    isDefined,
    NotificationIdentifier,
    TimeRecordingEntryModel,
} from '@evorait/pace-core';
import { FormIdentifier } from '@evorait/pace-forms';
import moment from 'moment';

/**
 * The payloads the submit pipeline hands its collaborators, built here rather than inside
 * `ConfirmationSubmitEffects`.
 *
 * Every function is a pure derivation over a confirmation and the config flags that gate it: whether
 * a mileage entry is owed, when the materials were consumed, which questionnaires to clean up. Keeping them here leaves each private method in the effect as its
 * collaborator calls in the legacy order plus one builder call, and lets the derivations be tested
 * without an effect, a store or an injector.
 *
 * They live beside the model converters rather than under `state/` because they take legacy models,
 * which the NgRx layer may not import (see `architecture-rules.md`).
 */

/**
 * When the materials were consumed: the end of the last on-site time-recording entry, falling back to
 * now when the technician recorded no on-site time.
 *
 * @param lastOsrEntry the last on-site entry, absent when there is none
 * @returns the consumption timestamp to store with the materials
 */
export function materialConsumedDate(lastOsrEntry: TimeRecordingEntryModel | undefined): moment.Moment {
    return isDefined(lastOsrEntry) ? lastOsrEntry.endDateTime.clone() : moment();
}

/** One questionnaire to clean up, as `cleanupSpqDocs` carries it. */
export type SpqMaterialCleanup = Readonly<{
    form: FormIdentifier;
    notificationId: NotificationIdentifier;
    materialKey: string;
}>;

/**
 * The service-part questionnaires a submitted confirmation leaves behind.
 *
 * Only materials that actually show a questionnaire have one to clean up, and each is addressed by the
 * notification it was consumed for — not the confirmation's, since a material may come from another.
 *
 * @param confirmation the confirmation being submitted
 * @param equipmentId the notification's main equipment number, which the questionnaire's form is keyed by
 * @returns the cleanups to dispatch, empty when there is nothing to clean up
 */
export function spqMaterialsFor(confirmation: ConfirmationModel, equipmentId: string): SpqMaterialCleanup[] {
    if (!confirmation.materials) {
        return [];
    }
    return confirmation.materials.listMaterial.value
        .filter((material) => material.showServicePartQuestionnaire())
        .map((material) => ({
            form: {
                equipmentId,
                formId: material.spqDocId,
                materialId: material.materialIdentifier.materialNumber.value,
            },
            notificationId: {
                qmNum: material.notificationIdentifier.qmNum,
                maNum: material.notificationIdentifier.maNum,
                frontId: material.notificationIdentifier.frontId,
            },
            materialKey: material.materialKey,
        }));
}

/**
 * Which safety-assessment form the notification's cleanup is about.
 *
 * @param safetyAssessmentFormId the configured form id
 * @param equipmentId the notification's main equipment number
 * @param materialId its material number
 * @returns the form identifier the forms service looks up in its cache
 */
export function safetyAssessmentFormIdentifier(
    safetyAssessmentFormId: string,
    equipmentId: string,
    materialId: string,
): FormIdentifier {
    return { formId: safetyAssessmentFormId, equipmentId, materialId };
}

/**
 * Whether a submit failure was the offline store refusing the save, which the technician is told about
 * differently: the work is queued rather than lost.
 *
 * @param error the error the submit rejected with
 * @returns `true` when it is the offline-store save error
 */
export function isOfflineSaveError(error: unknown): boolean {
    return error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR;
}
